import Fuse from 'fuse.js';
import type { IFuseOptions } from 'fuse.js';
import type { Vendor, Address } from '@stash/shared-types';

/** Maximum number of fuzzy results shown (keeps the dropdown scannable). */
export const MAX_RESULTS = 20;

const FUSE_OPTIONS: IFuseOptions<Vendor> = {
  keys: [
    { name: 'companyName', weight: 0.7 },
    { name: 'contactName', weight: 0.3 },
  ],
  threshold: 0.4,
  ignoreLocation: true,
};

/** Build a memoizable Fuse index over a vendor list. */
export function buildVendorFuse(vendors: Vendor[]): Fuse<Vendor> {
  return new Fuse(vendors, FUSE_OPTIONS);
}

interface SearchOptions {
  limit?: number;
  /** Pre-built Fuse index (memoized by the caller); built on demand if omitted. */
  fuse?: Fuse<Vendor>;
}

/**
 * Search vendors by name. An exact `recordID` match short-circuits to that one
 * vendor (preserving the paste-the-ID workflow); otherwise a fuzzy, typo-tolerant
 * search runs across `companyName` and `contactName`, ranked by Fuse score and
 * capped at {@link MAX_RESULTS}. Address is display-only and not a search key.
 */
export function searchVendors(vendors: Vendor[], query: string, options: SearchOptions = {}): Vendor[] {
  const q = query.trim();
  if (!q) return [];

  const exact = vendors.find((v) => v.recordID === q);
  if (exact) return [exact];

  const fuse = options.fuse ?? buildVendorFuse(vendors);
  const limit = options.limit ?? MAX_RESULTS;
  return fuse
    .search(q)
    .slice(0, limit)
    .map((result) => result.item);
}

/** The display company name, falling back when absent or blank. */
export function displayCompanyName(vendor: Vendor): string {
  const name = vendor.companyName?.trim();
  return name ? name : 'Unnamed Vendor';
}

export interface HighlightSegment {
  text: string;
  match: boolean;
}

/**
 * Split `text` into segments marking which parts match the query, for visual
 * highlighting in result rows. Each whitespace-separated query term (>= 2 chars)
 * is matched case-insensitively; the concatenated segment text always equals the
 * original `text`. Returns a single non-match segment when there is nothing to
 * highlight.
 */
export function highlightMatch(text: string, query: string): HighlightSegment[] {
  const terms = query.trim().toLowerCase().split(/\s+/).filter((t) => t.length >= 2);
  if (!text || terms.length === 0) return [{ text: text ?? '', match: false }];

  const lower = text.toLowerCase();
  const matched = new Array<boolean>(text.length).fill(false);
  for (const term of terms) {
    let idx = lower.indexOf(term);
    while (idx !== -1) {
      for (let i = idx; i < idx + term.length; i++) matched[i] = true;
      idx = lower.indexOf(term, idx + term.length);
    }
  }

  const segments: HighlightSegment[] = [];
  let current = '';
  let currentMatch = matched[0];
  for (let i = 0; i < text.length; i++) {
    if (matched[i] === currentMatch) {
      current += text[i];
    } else {
      segments.push({ text: current, match: currentMatch });
      current = text[i];
      currentMatch = matched[i];
    }
  }
  if (current) segments.push({ text: current, match: currentMatch });
  return segments;
}

/**
 * Compose a single-line display address from the structured `Address` parts
 * (the Vendor model has no preformatted address string). Empty parts are
 * skipped: "street[ street2], city, ST postalCode".
 */
export function composeAddress(address?: Address): string {
  if (!address) return '';

  const streetLine = [address.street, address.street2]
    .map((p) => p?.trim())
    .filter(Boolean)
    .join(' ');

  const cityState = [address.city, address.region]
    .map((p) => p?.trim())
    .filter(Boolean)
    .join(', ');

  const cityStateZip = [cityState, address.postalCode?.trim()].filter(Boolean).join(' ');

  return [streetLine, cityStateZip].filter(Boolean).join(', ');
}
